// src/components/admin/AdminImages.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Button,
} from "react-bootstrap";
import { useData } from "../../providers/dataProvider";
import ImageUploadModal from "../shared/ImageUploadModal";
import ImageEditModal from "../shared/ImageEditModal";
import ImageCard from "../shared/ImageCard";
import { useNavigate } from "react-router-dom";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

const AdminImages = () => {
  // Use the DataContext with our updated imagesModule
  const { images, imagesLoading, refreshImages, getImageUrl, removeImage } =
    useData();

  const [error, setError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [imageUrls, setImageUrls] = useState({});

  const navigate = useNavigate();

  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);

  // Load image URLs when images change
  useEffect(() => {
    if (!images) return;

    const urls = {};
    images.forEach((image) => {
      if (image.aws_location) {
        urls[image.id] = getImageUrl(image.aws_location);
      }
    });

    setImageUrls(urls);
  }, [images, getImageUrl]);

  const handleRefresh = async () => {
    try {
      setError(null);
      await refreshImages();
    } catch (err) {
      console.error("Error refreshing images:", err);
      setError(`Failed to load images: ${err.message}`);
    }
  };

  // Handle image deletion
  const handleDeleteImage = async (id) => {
    try {
      setError(null);
      await removeImage(id);
    } catch (err) {
      console.error("Error deleting image:", err);
      setError(`Failed to delete image: ${err.message}`);
    }
  };

  // Handle edit button click
  const handleEditClick = (imageId) => {
    setSelectedImageId(imageId);
    setShowEditModal(true);
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">Manage Images</h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/dashboard")}
          >
            Back to Dashboard
          </Button>
        </Col>
      </Row>
      <Card
        bg="dark"
        text="light"
        className="shadow"
        style={{ width: "100%", padding: 0 }}
      >
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-light"
              className="me-2"
              onClick={handleRefresh}
              disabled={imagesLoading}
            >
              Refresh
            </Button>
            <Button variant="success" onClick={() => setShowUploadModal(true)}>
              Upload New Image
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="p-3">
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          {imagesLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="light" />
              <p className="text-light mt-3">Loading images...</p>
            </div>
          ) : (
            <>
              {images && images.length > 0 ? (
                <div className="image-gallery-container p-2">
                  <Row xs={2} sm={3} md={4} lg={5} className="g-3">
                    {images.map((image) => (
                      <Col key={image.id}>
                        <ImageCard
                          image={image}
                          imageUrl={imageUrls[image.id]}
                          onEdit={handleEditClick}
                          onDelete={handleDeleteImage}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <Alert variant="info">
                  No images found. Upload some images to get started.
                </Alert>
              )}
            </>
          )}
        </Card.Body>

        <Card.Footer className="text-muted">
          <small>Total images: {images ? images.length : 0}</small>
        </Card.Footer>
      </Card>

      {/* Image Upload Modal Component */}
      <ImageUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onUploadSuccess={refreshImages}
      />

      {/* Image Edit Modal Component */}
      <ImageEditModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        imageId={selectedImageId}
        onUpdateSuccess={refreshImages}
      />
    </Container>
  );
};

export default AdminImages;
